import { useContext, useState, useCallback, useEffect, useRef, type SyntheticEvent } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import AddComponent from "./add-component";
import ConfirmationStep from "./confirmation-step";
import { useService } from "../organizations-service";
import pubSub, { EVENTS } from "../../../config/pubSub";
import { IAddEditProviderModel } from "../organization-upsert-form";
import { OrganizationsContext } from "../organizations-context";
import Typography from "@mui/material/Typography";
import { StyledButton } from "../../../components/styled-button/styled-button";
import { StyledDialogContent, StyledDialogTitle, StyledTypography } from "../../../components/shared-styles/dialog-title-styles";

const AddOrganizationComponent = () => {
  const [openAddOrganization, setOpenAddOrganization] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const { orgService } = useService();
  const [isAllowAddChild, setIsAllowAddChild] = useState<boolean>(false);
  const { isOrgListLoading, selectedOrg, setSelectedOrg} = useContext(OrganizationsContext);
  const { t } = useTranslation();

  const checkOrgLimit = useCallback(async () => {
    try {
      if (!orgService.getOrg) return;
      const res: any = await orgService.getOrgLimit();
      setIsAllowAddChild(res.childrenAdd);
    } catch (error) {
      pubSub.publish(EVENTS.ERROR, "common_error_msg");
    }
  }, [orgService]);

  useEffect(() => {
    checkOrgLimit();
  }, [checkOrgLimit]);

  const handleAddClickOpen = () => {
    checkOrgLimit();
    setOpenAddOrganization(isAllowAddChild);

    if (selectedOrg)
      setSelectedOrg("");
  };

  const handleAddOrganizationDone = () => {
    setOpenAddOrganization(false);
    setOpenConfirm(true);
  };

  return (
    <div>
      <StyledButton
        variant="contained"
        onClick={handleAddClickOpen}
        className="d-flex align-items-center"
        disabled={!isAllowAddChild || isOrgListLoading}
        startIcon={<AddCircleOutlineOutlinedIcon />}
      >
        {t("cmp_organization_add_button")}
      </StyledButton>
      <AddOrganizationStep
        open={openAddOrganization}
        handleAddOrganizationClose={() => setOpenAddOrganization(false)}
        handleAddOrganizationDone={handleAddOrganizationDone}
      />
      <ConfirmationStep
        open={openConfirm}
        handleConfirmClose={() => setOpenConfirm(false)}
      />
    </div>
  );
};

type IAddOrganizationProps = {
  open: boolean;
  handleAddOrganizationClose: () => void;
  handleAddOrganizationDone: () => void;
};

const AddOrganizationStep = (props: IAddOrganizationProps) => {
  const { t } = useTranslation();
  const { setSelectedOrg, setSelectedCheckedOrgs } = useContext(OrganizationsContext);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const isProcessingRef = useRef<boolean>(false);

  const defaultModel: IAddEditProviderModel = {
    0: {
      name: "",
      displayName: "",
      email: "",
      url: "",
      locale: "en",
    },
    isValid: false
  };

  const [model, setModel] = useState<IAddEditProviderModel>(defaultModel);

  const handleClose = () => {
    setSelectedOrg(null);
    setSelectedCheckedOrgs([]);
    props.handleAddOrganizationClose();
    setModel(defaultModel);
  };

  const handleModelChange = (updatedModel: any) => {
    const updated = updatedModel;
    setModel((prevModel) => ({
      ...prevModel,
      ...updated,
    }));
  };

  const stopInteractionsWhenProcessing = (event: SyntheticEvent) => {
    if (!isProcessingRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  const handleProcessingChange = (processing: boolean) => {
    isProcessingRef.current = processing;
    setIsProcessing(processing);
  };

  return (
    <Dialog
      open={props.open}
      onKeyDownCapture={stopInteractionsWhenProcessing}
      onKeyUpCapture={stopInteractionsWhenProcessing}
      onMouseDownCapture={stopInteractionsWhenProcessing}
      onPointerDownCapture={stopInteractionsWhenProcessing}
      PaperProps={{
        style: {
          maxWidth: '701px', // Set custom max width
          pointerEvents: isProcessing ? 'none' : 'auto',
        },
          }}
      fullWidth={true}
      onClose={(event, reason) => {
        if (!isProcessingRef.current && reason !== 'backdropClick') {
          handleClose();
        }
      }}
      aria-labelledby="form-dialog-title"
      disableEscapeKeyDown={isProcessing}
    >
      <StyledDialogTitle id="form-dialog-title">
        <StyledTypography>{t("cmp_organization_add_button")}</StyledTypography>
      </StyledDialogTitle>
      <StyledDialogContent
        onClickCapture={stopInteractionsWhenProcessing}
        onKeyDownCapture={stopInteractionsWhenProcessing}
      >
        <AddComponent
          handleDone={() => {
            handleClose();
            props.handleAddOrganizationDone();
          }}
          handleClose={handleClose}
          model={model}
          handleModelChange={handleModelChange}
          handleProcessingChange={handleProcessingChange}
        />
      </StyledDialogContent>
    </Dialog>
  );
};

export default AddOrganizationComponent;
